import { Flex } from '@chakra-ui/layout';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CellContext,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { remToPx } from 'packages/common-frontend/theme/utils';
import { useExpenseDiffResult } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/[expenseId]/hooks';
import { deleteExpense } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/expense-mechanics/dataMappings';
import { useConfirmDeleteContext } from '~/core/routes/settings/implementations/common/ConfirmDeleteModalContext';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  DiffTag,
  ShowWhileDiffing,
} from '~/core/routes/settings/implementations/common/DiffInputs';

type TableRowData = CreditAgreement.DomainEntity.LiabilityAccountProperty & {
  counterpartyName: string;
};

const EditExpenseMenu = ({ row }: CellContext<TableRowData, any>) => {
  const menuControls = useDisclosure();
  const { showConfirmDeleteModal } = useConfirmDeleteContext();

  const updateCreditAgreement = useCreditAgreementStore(
    (store) => store.updateCreditAgreement,
  );

  const onDeleteExpense = useCallback(
    (counterpartySlug: string, tAccount: string) => {
      if (tAccount === 'unusedInterestExpense') {
        updateCreditAgreement((d) =>
          deleteExpense(d, counterpartySlug, tAccount),
        );
      }
    },
    [updateCreditAgreement],
  );

  return (
    <Flex
      justify="right"
      onClick={(e) => e.stopPropagation()}
      overflow="hidden"
    >
      <Menu {...menuControls} computePositionOnMount strategy="fixed">
        <MenuButton
          as={IconButtonV2}
          variant="secondary-muted"
          colorScheme="gray"
          isActive={menuControls.isOpen}
          aria-label="Facility options"
          icon={<Icon icon="ellipsis-vertical" />}
          tooltipEnabled={false}
        />
        <MenuList zIndex="dropdown">
          <MenuItem
            onClick={() =>
              showConfirmDeleteModal(row.original.name, () => {
                onDeleteExpense(
                  row.original.counterpartySlug,
                  row.original.tAccount,
                );
              })
            }
          >
            Delete Fee
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};

const RenderExpenseName = ({
  row,
  getValue,
}: CellContext<TableRowData, 'name'>) => {
  const diffResult = useExpenseDiffResult(
    row.original.counterpartySlug,
    row.original.tAccount,
  );

  return (
    <Flex gap="1rem" alignItems="baseline">
      <ShowWhileDiffing>
        <DiffTag diff={diffResult?.diffResult} disableTooltip />
      </ShowWhileDiffing>
      {getValue()}
    </Flex>
  );
};

const columnHelper = createColumnHelper<TableRowData>();

const columnDefs = [
  columnHelper.accessor('name', {
    header: 'Fee Name',
    enableSorting: false,
    cell: RenderExpenseName,
  }),
  columnHelper.accessor('type', {
    header: 'Fee Type',
    enableSorting: false,
    cell: () => 'Fixed',
  }),
  columnHelper.accessor('counterpartyName', {
    header: 'Recipient',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('value', {
    header: 'Value',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.display({
    id: 'editFee',
    header: '',
    enableSorting: false,
    minSize: remToPx(1),
    size: remToPx(1),
    cell: EditExpenseMenu,
  }),
];

export const ExpensesTable = () => {
  const creditAgreement = useCreditAgreementStore(
    (store) => store.creditAgreement,
  );

  const tableData = useMemo(
    () =>
      creditAgreement?.liabilityAccounts?.map((account) => {
        const capitalProvider = creditAgreement?.facilityMechanics.parties.capitalProviders?.find(
          (p) => p.entity.slug === account.counterpartySlug,
        );
        return {
          ...account,
          counterpartyName: capitalProvider?.entity.name ?? '',
        };
      }) ?? [],
    [creditAgreement],
  );

  const table = useReactTable({
    data: tableData,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  });

  const navigate = useNavigate();

  return (
    <TableBETA
      table={table}
      onRowClick={(row) => {
        if (row.original.tAccount === 'unusedInterestExpense') {
          navigate(`unusedFee/${row.original.counterpartySlug}`);
        }
      }}
    />
  );
};
